<script setup lang="ts">
  import { inject, ref, watch } from 'vue';
  import type { OrderOptions, Pagination as PaginationMeta } from '@/shared/lib';
  import {
    Checkbox,
    HeaderTableWithButtons,
    InfiniteScrollView,
    Pagination,
    SearchNoResults,
    SelectInput,
    SimpleLoading,
    TableHeader,
  } from '@/shared/ui';

  import type { DocumentRevision } from '@/entities/Document';
  import type {
    DocumentSelectionMode,
    DocumentSelectionRow,
  } from '../../model/useDocumentSelectionModal';
  import type { PreviousRevisionsState } from '../../model/usePreviousRevisionsCache';
  import {
    createDocumentSelectionFilterContext,
    DocumentSelectionFilterInjectionKey,
  } from '../../model/documentSelectionFilterContext';
  import DocumentSelectionFilterPanel from '../filters/DocumentSelectionFilterPanel.vue';
  import DocumentSelectionRevisionRow from './DocumentSelectionRevisionRow.vue';

  const props = withDefaults(
    defineProps<{
      amountOptions: Array<{ id: number; name: string }>;
      canAddMore: boolean;
      canSelectRevision: (revision: DocumentRevision) => boolean;
      currentPage: number;
      documentRows: DocumentSelectionRow[];
      formatRevisionLabel: (revision: number | undefined) => string;
      isDocumentExpanded: (row: DocumentSelectionRow) => boolean;
      isRevisionActive: (revisionId: number) => boolean;
      isRevisionAlreadyAdded: (revisionId: number) => boolean;
      isRevisionSelected: (revisionId: number) => boolean;
      loading: boolean;
      selectedAmount: number;
      selectionMode: DocumentSelectionMode;
      tableTitle: string;
      allVisibleRevisionsSelected?: boolean;
      isFilterActive?: boolean;
      isInfiniteScrollMode?: boolean;
      onDemandRevisions?: boolean;
      getPreviousRevisionsState?: (documentId: number) => PreviousRevisionsState;
      metadata?: PaginationMeta;
      orderOptions?: OrderOptions[];
      hasMoreRevisions?: boolean;
      showAdvancedFilter?: boolean;
      showSelectAll?: boolean;
      showSort?: boolean;
      showViewAction?: boolean;
      loadingMore?: boolean;
    }>(),
    {
      hasMoreRevisions: false,
      allVisibleRevisionsSelected: false,
      isFilterActive: false,
      isInfiniteScrollMode: false,
      onDemandRevisions: false,
      getPreviousRevisionsState: () => ({ status: 'idle' as const, revisions: [] }),
      loadingMore: false,
      metadata: undefined,
      orderOptions: () => [],
      showAdvancedFilter: false,
      showSelectAll: false,
      showSort: false,
      showViewAction: false,
    }
  );

  const emit = defineEmits<{
    (e: 'row-click', revision: DocumentRevision): void;
    (e: 'toggle', revision: DocumentRevision): void;
    (e: 'toggle-all'): void;
    (e: 'expand', documentId: number): void;
    (e: 'update:sort', sortField: string, orderFlow: string): void;
    (e: 'update:amount', value: string | number): void;
    (e: 'page', value: number): void;
    (e: 'view', revision: DocumentRevision): void;
    (e: 'retry-previous-revisions', documentId: number, excludeRevisionId: number): void;
  }>();

  function showsChevron(row: DocumentSelectionRow): boolean {
    if (!props.onDemandRevisions) {
      return row.olderRevisions.length > 0;
    }

    const state = props.getPreviousRevisionsState(row.documentId);
    if (state.status === 'loaded') {
      return state.revisions.length > 0;
    }

    return row.representingRevision.hasPreviousRevisions ?? false;
  }

  const { activeFilters, isFilterPanelOpen, toggleFilterPanel, applyFilters } = inject(
    DocumentSelectionFilterInjectionKey,
    () => createDocumentSelectionFilterContext(),
    true
  );

  const stableDocumentRows = ref(props.documentRows);
  const stableMetadata = ref(props.metadata);

  watch(
    [() => props.documentRows, () => props.loading],
    ([rows, loading]) => {
      if (rows.length > 0 || !loading) {
        stableDocumentRows.value = rows;
      }
    }
  );

  watch(
    [() => props.metadata, () => props.loading],
    ([metadata, loading]) => {
      if (!loading || (metadata?.total ?? 0) > 0) {
        stableMetadata.value = metadata;
      }
    }
  );
</script>

<template>
  <div class="mt-6">
    <div
      data-testid="default-table"
      class="overflow-y-visible relative overflow-x-auto rounded-2xl border border-gray-02 bg-white-01"
    >
      <div
        v-if="loading && stableDocumentRows.length > 0"
        data-testid="document-selection-table-loading-overlay"
        class="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white-01/70"
      >
        <SimpleLoading />
      </div>

      <table class="min-w-full table-fixed">
        <colgroup>
          <col class="w-[38px]" />
          <col class="w-16" />
          <col />
          <col class="w-22" />
          <col class="w-33" />
        </colgroup>
        <thead>
          <tr>
            <HeaderTableWithButtons
              colspan="5"
              :text="tableTitle"
              :bold-title="false"
              :order-options="showSort ? orderOptions : []"
              order-option-class="font-normal"
              :has-filter="showAdvancedFilter"
              :is-filter-active="isFilterActive || isFilterPanelOpen"
              @update:sort="(sortField, orderFlow) => emit('update:sort', sortField, orderFlow)"
              @filter-clicked="toggleFilterPanel"
            >
              <template #extra-controls>
                <SelectInput
                  :model-value="selectedAmount"
                  :options="amountOptions"
                  container-class="w-auto"
                  button-class="bg-white-01 border border-gray-02 whitespace-nowrap shadow-xs font-normal"
                  @update:model-value="emit('update:amount', $event)"
                />
              </template>
            </HeaderTableWithButtons>
          </tr>
          <tr v-if="showAdvancedFilter">
            <th
              colspan="5"
              class="p-0 transition-[border-color] duration-300 ease-in-out"
              :class="
                isFilterPanelOpen ? 'border-b-2 border-(--theme-secondary-ink)' : 'border-b-0 border-transparent'
              "
            >
              <div
                data-testid="document-selection-filter-panel-wrapper"
                class="grid transition-[grid-template-rows] duration-300 ease-in-out"
                :class="isFilterPanelOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
                :inert="isFilterPanelOpen ? undefined : true"
              >
                <div class="overflow-hidden">
                  <DocumentSelectionFilterPanel
                    :current-filters="activeFilters"
                    @clear="applyFilters({})"
                    @apply="applyFilters"
                  />
                </div>
              </div>
            </th>
          </tr>
          <tr v-if="stableDocumentRows.length > 0">
            <th colspan="2" class="pl-6 pr-4 py-4.5 text-left text-xs font-semibold text-gray-07">
              <div class="flex items-center gap-3">
                <Checkbox
                  v-if="showSelectAll && selectionMode === 'multiple'"
                  id="select-all-revisions"
                  :checked="allVisibleRevisionsSelected"
                  @click.stop
                  @toggle="emit('toggle-all')"
                />
                <span>Prefixo</span>
              </div>
            </th>
            <TableHeader
              text="Título"
              th-class="px-4 py-4.5 text-left text-xs font-semibold text-gray-07"
            />
            <TableHeader
              text="Rev."
              th-class="px-4 py-4.5 text-left text-xs font-semibold text-gray-07"
            />
            <TableHeader
              :text="showViewAction ? 'Ações' : ''"
              th-class="px-4 py-4.5 text-left text-xs font-semibold text-gray-07"
            />
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-02 border-t border-gray-02 bg-white-01">
          <tr v-if="loading && stableDocumentRows.length === 0">
            <td colspan="5">
              <div class="flex min-h-40 items-center justify-center">
                <SimpleLoading />
              </div>
            </td>
          </tr>
          <template v-else-if="stableDocumentRows.length > 0">
            <template v-for="row in stableDocumentRows" :key="row.documentId">
              <DocumentSelectionRevisionRow
                :revision="row.representingRevision"
                :selection-mode="selectionMode"
                :format-revision-label="formatRevisionLabel"
                :is-active="isRevisionActive(row.representingRevision.id)"
                :is-selected="isRevisionSelected(row.representingRevision.id)"
                :is-already-added="isRevisionAlreadyAdded(row.representingRevision.id)"
                :can-select="canSelectRevision(row.representingRevision)"
                :show-view-action="showViewAction"
                :show-expand-button="showsChevron(row)"
                :is-expanded="isDocumentExpanded(row)"
                action-icon-class="h-6 w-6"
                :row-class="isRevisionAlreadyAdded(row.representingRevision.id) ? 'bg-gray-01' : ''"
                @row-click="emit('row-click', $event)"
                @toggle="emit('toggle', $event)"
                @view="emit('view', $event)"
                @expand="emit('expand', row.documentId)"
              />

              <template v-if="!onDemandRevisions">
                <DocumentSelectionRevisionRow
                  v-for="revision in isDocumentExpanded(row) ? row.olderRevisions : []"
                  :key="revision.id"
                  :revision="revision"
                  :selection-mode="selectionMode"
                  :format-revision-label="formatRevisionLabel"
                  :is-active="isRevisionActive(revision.id)"
                  :is-selected="isRevisionSelected(revision.id)"
                  :is-already-added="isRevisionAlreadyAdded(revision.id)"
                  :can-select="canSelectRevision(revision)"
                  :show-view-action="showViewAction"
                  :is-older-revision="true"
                  action-icon-class="h-6 w-6"
                  row-class="bg-gray-04"
                  @row-click="emit('row-click', $event)"
                  @toggle="emit('toggle', $event)"
                  @view="emit('view', $event)"
                />
              </template>

              <template v-else-if="isDocumentExpanded(row)">
                <tr
                  v-if="getPreviousRevisionsState(row.documentId).status === 'loading'"
                  :data-testid="`previous-revisions-loading-${row.documentId}`"
                >
                  <td colspan="5" class="py-4 text-center text-sm text-gray-06">
                    Carregando revisões anteriores...
                  </td>
                </tr>
                <tr
                  v-else-if="getPreviousRevisionsState(row.documentId).status === 'error'"
                  :data-testid="`previous-revisions-error-${row.documentId}`"
                >
                  <td colspan="5" class="py-4 text-center text-sm text-red-600">
                    Não foi possível carregar as revisões anteriores.
                    <button
                      type="button"
                      class="ml-2 underline"
                      @click="emit('retry-previous-revisions', row.documentId, row.representingRevision.id)"
                    >
                      Tentar novamente
                    </button>
                  </td>
                </tr>
                <DocumentSelectionRevisionRow
                  v-for="revision in getPreviousRevisionsState(row.documentId).status === 'loaded'
                    ? getPreviousRevisionsState(row.documentId).revisions
                    : []"
                  :key="revision.id"
                  :revision="revision"
                  :selection-mode="selectionMode"
                  :format-revision-label="formatRevisionLabel"
                  :is-active="isRevisionActive(revision.id)"
                  :is-selected="isRevisionSelected(revision.id)"
                  :is-already-added="isRevisionAlreadyAdded(revision.id)"
                  :can-select="canSelectRevision(revision)"
                  :show-view-action="showViewAction"
                  :is-older-revision="true"
                  action-icon-class="h-6 w-6"
                  row-class="bg-gray-04"
                  @row-click="emit('row-click', $event)"
                  @toggle="emit('toggle', $event)"
                  @view="emit('view', $event)"
                />
              </template>
            </template>
          </template>
          <tr v-else>
            <td colspan="5" class="px-6 py-12">
              <SearchNoResults />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <InfiniteScrollView
      v-if="isInfiniteScrollMode && stableMetadata && stableDocumentRows.length > 0"
      :all-items="stableDocumentRows"
      :loading-more="loadingMore"
      :has-more-items="hasMoreRevisions"
      :metadata="stableMetadata"
      entity-name="documento"
    />

    <Pagination
      v-else-if="stableMetadata && stableDocumentRows.length > 0"
      :total-items="stableMetadata.total"
      :items-per-page="selectedAmount"
      :current-page="currentPage"
      entity-name="documento"
      @page-changed="emit('page', $event)"
    />
  </div>
</template>
